import { useQuery } from '@tanstack/react-query';

import { analyticsApi } from '@/api';
import { Card, CardHeader, LinkButton } from '@/components/ui';
import { days, percent } from '@/features/analytics/format';
import { paths } from '@/routes/paths';

const PERIOD_DAYS = 30;

/**
 * Recruitment performance in brief: how fast and how well hiring is going,
 * as five rates from the same endpoints the Analytics page reads. Counts
 * live in `RecruitmentSummary` above it; this card is the "how well".
 */
export function RecruitmentPerformance() {
  const overview = useQuery({
    // Shared with `RecruitmentSummary`, so the overview is fetched once.
    queryKey: ['company-analytics-overview', PERIOD_DAYS],
    queryFn: () => analyticsApi.getCompanyOverview(PERIOD_DAYS),
  });
  const outcomes = useQuery({
    queryKey: ['company-analytics', 'outcomes', PERIOD_DAYS],
    queryFn: () => analyticsApi.getOutcomes(PERIOD_DAYS),
  });

  // Quietly absent on failure, like the summary: the Analytics page reports it.
  if (overview.isError) return null;

  const loading = overview.isPending;
  const speed = overview.data?.speed ?? {};
  const funnel = overview.data?.funnel ?? [];
  const hired = funnel[funnel.length - 1];
  const show = (text) => (loading ? '…' : text);

  const metrics = [
    {
      label: 'Time to hire',
      value: show(days(speed.median_days_to_hire)),
      hint: 'Application to accepted offer',
    },
    {
      label: 'First response',
      value: show(days(speed.median_days_to_first_response)),
      hint: 'Median wait for any answer',
    },
    {
      label: 'Response rate',
      value: show(percent(speed.response_rate)),
      hint: 'Applications answered',
    },
    {
      label: 'Interview acceptance',
      value: outcomes.isPending ? '…' : percent(outcomes.data?.interviews?.acceptance_rate),
      hint: 'Invitations accepted',
    },
    {
      label: 'Offer acceptance',
      value: outcomes.isPending ? '…' : percent(outcomes.data?.offers?.acceptance_rate),
      hint: 'Offers accepted',
    },
    {
      label: 'Applied to hired',
      value: show(percent(hired?.rate_from_top)),
      hint: 'Overall conversion',
    },
  ];

  return (
    <Card className="mt-6">
      <CardHeader
        title="Recruitment performance"
        description={`How fast and how well you hired in the last ${PERIOD_DAYS} days.`}
        action={
          <LinkButton to={paths.company.analytics} size="sm" variant="secondary">
            Full analytics
          </LinkButton>
        }
      />
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {metrics.map((metric) => (
          <div key={metric.label} className="bg-surface-muted rounded-lg px-3 py-3">
            <dt className="text-ink-muted text-[11px] font-medium tracking-wide uppercase">
              {metric.label}
            </dt>
            <dd className="text-ink mt-1 text-lg font-semibold tabular-nums">{metric.value}</dd>
            <dd className="text-ink-muted text-xs">{metric.hint}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}
